import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Icon } from '@/components/Icon';
import { useSession } from '@/stores/session';
import { NAV, canSeeNavItem } from './nav';
import s from './KeyboardShortcuts.module.css';

type Shortcut = { label: string; keys: string[]; pending?: boolean };
const GROUPS: { title: string; shortcuts: Shortcut[] }[] = [
  { title: '全局', shortcuts: [
    { label: '全局搜索', keys: ['/'] },
    { label: '新建订单', keys: ['N'] },
    { label: '打开通知', keys: ['⇧', 'N'] },
    { label: '切换模块（按 rail 顺序）', keys: ['⌥', '1–9'] },
    { label: '收起 / 展开订单列表栏', keys: ['['] },
    { label: '快捷键一览', keys: ['?'] },
    { label: '关闭浮层', keys: ['esc'] },
  ] },
  { title: '列表', shortcuts: [
    { label: '订单列表下一条 / 上一条', keys: ['J', 'K'] },
    { label: '打开', keys: ['↵'] },
    { label: '勾选当前行', keys: ['X'] },
    { label: '全选已加载', keys: ['⌘', 'A'] },
    { label: '筛选', keys: ['F'] },
    { label: '刷新', keys: ['R'] },
    { label: '切换视图', keys: ['V'] },
  ] },
  { title: '详情与表单', shortcuts: [
    { label: '订单详情下一张 / 上一张', keys: ['⇧', 'J / K'] },
    { label: '切换页签', keys: ['1–6'] },
    { label: '复制订单号', keys: ['C'] },
    { label: '提交表单', keys: ['⌘', '↵'] },
    { label: '存草稿', keys: ['⌘', 'S'] },
    { label: '关闭（有改动先问）', keys: ['esc'] },
  ] },
];

function editing(target: EventTarget | null) {
  return target instanceof HTMLElement && (target.isContentEditable || !!target.closest('input, textarea, select, [contenteditable="true"]'));
}

export function KeyboardShortcuts() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const can = useSession((state) => state.can);
  const role = useSession((state) => state.user?.role);

  useEffect(() => {
    const openPanel = () => setOpen(true);
    window.addEventListener('ims:v2:keyboard-shortcuts', openPanel);
    return () => window.removeEventListener('ims:v2:keyboard-shortcuts', openPanel);
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open) { event.preventDefault(); setOpen(false); return; }
      if (editing(event.target) || event.metaKey || event.ctrlKey || open) return;
      if (event.key === '?' && !event.altKey) { event.preventDefault(); setOpen(true); return; }
      if (event.key.toLowerCase() === 'n' && event.shiftKey && !event.altKey) { event.preventDefault(); navigate('/notifications'); return; }
      if (event.key.toLowerCase() === 'n' && !event.shiftKey && !event.altKey && can('orders.create')) { event.preventDefault(); navigate('/orders/new'); return; }
      if (event.altKey && !event.shiftKey && /^[1-9]$/.test(event.key)) {
        const target = NAV.filter((item) => canSeeNavItem(item, can, role) && item.to)[Number(event.key) - 1];
        if (target?.to) { event.preventDefault(); navigate(target.to); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [can, navigate, open, role]);

  if (!open) return null;
  return <div className={s.scrim} onMouseDown={(event) => { if (event.target === event.currentTarget) setOpen(false); }}>
    <section className={s.panel} role="dialog" aria-modal="true" aria-label="键盘快捷键">
      <header className={s.header}><Icon name="kbd" size={20} /><strong>键盘快捷键</strong><small>外接键盘可用 · 随时按 ? 打开</small><button type="button" aria-label="关闭快捷键" onClick={() => setOpen(false)}><Icon name="x" size={20} /></button></header>
      <div className={s.groups}>{GROUPS.map((group) => <div className={s.group} key={group.title}><h2>{group.title}</h2>{group.shortcuts.map((item) => <div className={`${s.row} ${item.pending ? s.pending : ''}`} key={item.label} title={item.pending ? '此快捷键尚未接入 V2' : undefined}><span>{item.label}{item.pending && <em>待接入</em>}</span><span className={s.keys}>{item.keys.map((key) => <kbd key={key}>{key}</kbd>)}</span></div>)}</div>)}</div>
      <footer>单键快捷键在输入框里不生效，不会吞掉你打的字。按住 ⌘ 出现的是 iPadOS 的系统菜单，网页加不进去，所以用 ? 打开这一页。</footer>
    </section>
  </div>;
}
